import apiConfig from 'constants/apiConfig';
import React, { createContext, useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from 'react-redux';
import type { Store } from 'redux';
import { useSseStream } from 'hooks/useSseStream';
import { useBrowserAuth } from './BrowserAuthProvider';

interface AppEventsContextType {
  needsRefresh: boolean;
  isConnected: boolean;
}

const AppEventsContext = createContext<AppEventsContextType | undefined>(
  undefined
);

const validEndpoints = {
  '/dice/editgroup': ['groups'],
  '/dice/deleteGroup': ['groups'],
  '/dice/inviteTogroup': ['groups', 'users'],
  '/dice/removeUserFromGroup': ['groups', 'users'],
  'dice/roles/createRoleWithPermissions': ['groups', 'roles'],
  'dice/roles/unAssignUserFromRole': ['groups', 'roles', 'users'],
  '/dice/roles/assignUserToRole': ['groups', 'roles', 'users'],
  '/dice/roles/editRoleAndPermissions': ['groups', 'roles'],
};

export const AppEventsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const queryClient = useQueryClient();
  const store: Store = useStore();
  const { getAccessToken } = useBrowserAuth();

  function invalidateRelatedQueries(endpoint: string) {
    const queriesToInvalidate =
      validEndpoints[endpoint as keyof typeof validEndpoints];
    if (queriesToInvalidate) {
      queriesToInvalidate.forEach((queryType) => {
        queryClient.invalidateQueries({ queryKey: [queryType] });
      });
    }
  }

  function handlePayload(payload: Record<string, unknown>) {
    if (payload.type === 'connected') return;
    if (!payload.endpoint) return;

    const currStore = store.getState();
    const userId = currStore.app.user.user_guid;
    const groups = currStore.app.groups;
    if (Array.isArray(groups) && payload.group) {
      const implicatedGroup = groups.find(
        (group: Group) => group.group_guid === payload.group
      );

      if (implicatedGroup && userId && userId !== payload.requesting_user) {
        invalidateRelatedQueries(payload.endpoint as string);
        setNeedsRefresh(true);
      }
    }
  }

  const { isConnected } = useSseStream<Record<string, unknown>>({
    url: apiConfig.apiGateway.eventsStream,
    onEvent: handlePayload,
    getToken: () => getAccessToken(),
    getIsTeams: () => {
      // Mirror useAxiosInterceptor's Teams detection.
      const authState = store.getState().auth;
      return (
        sessionStorage['isUsingTeams'] === 'true' ||
        authState?.teamsInitialized === true
      );
    },
    logPrefix: '[events-sse]',
    maxReconnectAttempts: 4,
    reconnectDelayMs: (attempt) => attempt * 3000,
    reconnectLogLevel: 'warn',
  });

  return (
    <AppEventsContext.Provider
      value={{
        needsRefresh: needsRefresh,
        isConnected: isConnected,
      }}
    >
      {children}
    </AppEventsContext.Provider>
  );
};

export const useAppEvents = () => {
  const context = useContext(AppEventsContext);
  if (!context) {
    throw new Error('useAppEvents must be used within AppEventsProvider');
  }
  return context;
};
